import { DateTime } from 'luxon'
import { CollectDataStepType } from 'src/constants/collect-data/enums'
import type { ReviewFormValues } from 'src/interfaces/documentReview'

type TemporaryLicenseValues = Pick<
  ReviewFormValues,
  'firstName' | 'lastName' | 'addressStreet1' | 'addressCity' | 'addressState' | 'addressPostalCode'
>

export interface ApproveGateInput {
  stepType: CollectDataStepType
  documentCount: number
  /** Form value. Empty string means the reviewer left it blank. */
  expiresAt: string
  effectiveAt: string
  isExpirationDateRequired: boolean
  isTemporaryDriverLicense: boolean
  /** Read only when `isTemporaryDriverLicense` — the backend 400s a temporary approval without them. */
  temporaryLicenseValues: TemporaryLicenseValues
}

/** Labels match the form fields, so the block message names exactly what to fill in. */
const resolveMissingTemporaryLicenseFields = (
  values: TemporaryLicenseValues,
  expiresAt: string,
): string[] => {
  const requiredFields: Array<{ label: string; value: string }> = [
    { label: 'First name', value: values.firstName },
    { label: 'Last name', value: values.lastName },
    { label: 'Expiration date', value: expiresAt },
    { label: 'Street 1', value: values.addressStreet1 },
    { label: 'City', value: values.addressCity },
    { label: 'State', value: values.addressState },
    { label: 'Postal code', value: values.addressPostalCode },
  ]

  return requiredFields
    .filter((field) => {
      return field.value.trim() === ''
    })
    .map((field) => {
      return field.label
    })
}

/**
 * Why this approval cannot be submitted, or null when it can.
 *
 * Returns a message rather than a boolean because a disabled button with no explanation is what
 * made the Retool version hard to work — the caller renders this string next to the control.
 */
export const resolveApproveBlockReason = ({
  stepType,
  documentCount,
  expiresAt,
  effectiveAt,
  isExpirationDateRequired,
  isTemporaryDriverLicense,
  temporaryLicenseValues,
}: ApproveGateInput): string | null => {
  if (stepType === CollectDataStepType.PIICollection && documentCount === 0) {
    return 'No document was uploaded — nothing to approve.'
  }

  // Parsed and compared in UTC, as the backend does — a reviewer's local calendar must not pass a
  // date the backend then rejects (or block one it accepts).
  const expiration = expiresAt === '' ? null : DateTime.fromISO(expiresAt, { zone: 'utc' })

  if (expiration !== null && !expiration.isValid) {
    return 'Enter a valid expiration date.'
  }

  // The backend's `isDateBeforeToday`: only a UTC calendar day before today's is in the past, so
  // an expiry of today is accepted.
  if (expiration && expiration.startOf('day') < DateTime.utc().startOf('day')) {
    return 'Expiration date is in the past.'
  }

  if (expiration && effectiveAt !== '') {
    const effective = DateTime.fromISO(effectiveAt, { zone: 'utc' })

    // The backend enforces @IsDateAfter('effectiveAt'); checking here turns a 400 into an inline
    // message the reviewer can act on.
    if (effective.isValid && expiration <= effective) {
      return 'Expiration date must be after the effective date.'
    }
  }

  if (isTemporaryDriverLicense) {
    // A temporary license needs the name, address and expiry whatever `isExpirationDateRequired`
    // says: on approval the backend writes them back to the contractor and 400s without them.
    const missingFields = resolveMissingTemporaryLicenseFields(temporaryLicenseValues, expiresAt)

    if (missingFields.length > 0) {
      return `Missing for a temporary license: ${missingFields.join(', ')}.`
    }

    // Stricter than the general rule: `validateTemporaryDriverLicenseReview` wants the expiry,
    // read as midnight UTC, after the current instant — so today never passes.
    if (expiration && expiration <= DateTime.utc()) {
      return 'Expiration date must be in the future for a temporary license.'
    }
  }

  if (expiration === null && isExpirationDateRequired) {
    return 'Add an expiration date to approve.'
  }

  return null
}

/**
 * The master-contractor rule, which has no exemptions at all:
 * `PartnerCollectedDataApproveReviewMetadataDto` marks `expiresAt` `@IsString @IsNotEmpty`, so
 * Retool's sign_contract / business_entity exemption would now 400. See the design's divergence #4.
 */
export const resolveMcApproveBlockReason = (expiresAt: string): string | null => {
  if (expiresAt === '') {
    return 'Add an expiration date to approve.'
  }

  const expiration = DateTime.fromISO(expiresAt)

  if (!expiration.isValid || expiration <= DateTime.now()) {
    return 'Expiration date must be in the future.'
  }

  return null
}
